"use client";

import { useState, useEffect } from "react";
import { useSession as useNextAuthSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation";
import { useToast } from "@/components/ui/use-toast";
import { resolveAiApiError } from "@/lib/ai-ui-error";

export default function TranslationPage() {
  const t = useTranslations("translation");
  const tAi = useTranslations("ai");
  const { toast } = useToast();
  const [mounted, setMounted] = useState(false);
  const { status } = useNextAuthSession();
  const router = useRouter();
  const params = useParams<{ locale: string }>();
  const locale = (params?.locale as string) || "zh-CN";
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [source, setSource] = useState("");
  const [translation, setTranslation] = useState("");
  const [result, setResult] = useState<{
    score: number;
    feedback: string;
    suggestions: string[];
    accuracy: number;
    fluency: number;
    completeness: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setMounted(true);
  }, []);

  // 如果用户未登录，重定向到登录页面（统一指向 /auth/signin）
  useEffect(() => {
    if (status === "unauthenticated") {
      const qs = searchParams?.toString();
      const callbackUrl = `${pathname}${qs ? `?${qs}` : ""}`;
      router.push(
        `/${locale}/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}&reason=auth_required`,
      );
    }
  }, [status, router, locale, pathname, searchParams]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!source.trim() || !translation.trim()) {
      setError(t("input_required"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/translation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          originalText: source,
          userTranslation: translation,
          direction: "zh-to-en",
        }),
      });

      if (res.status === 401) {
        const qs = searchParams?.toString();
        const callbackUrl = `${pathname}${qs ? `?${qs}` : ""}`;
        router.push(
          `/${locale}/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}&reason=auth_required`,
        );
        return;
      }

      const data = await res.json().catch(() => ({}) as any);

      if (!res.ok) {
        const resolved = resolveAiApiError({
          status: res.status,
          body: data,
          headers: res.headers,
          t: (key: string, values?: Record<string, any>) =>
            tAi(key as any, values ?? {}),
          fallback: t("error"),
        });
        setError(resolved.message);
        toast({ title: resolved.message, variant: "destructive" });
        return;
      }

      setResult((data?.evaluation ?? null) as any);
    } catch (err) {
      const fallback = t("error");
      setError(fallback);
      toast({ title: fallback, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // 如果组件未挂载，显示加载中
  if (!mounted) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="space-y-6">
            <div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  // 如果会话正在加载，显示加载状态
  if (status === "loading") {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="space-y-6">
            <div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto p-6 bg-background text-foreground"
    >
      <div className="mb-4">
        <label htmlFor="source-text" className="block text-foreground mb-2">
          原文
        </label>
        <textarea
          id="source-text"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          rows={3}
          className="w-full p-2 border border-border rounded bg-background text-foreground"
        />
      </div>
      <div className="mb-4">
        <label htmlFor="my-translation" className="block text-foreground mb-2">
          我的翻译
        </label>
        <textarea
          id="my-translation"
          value={translation}
          onChange={(e) => setTranslation(e.target.value)}
          rows={3}
          className="w-full p-2 border border-border rounded bg-background text-foreground"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 bg-primary text-primary-foreground rounded disabled:opacity-50"
      >
        {loading ? "评估中..." : "评估翻译"}
      </button>
      {error && <div className="mt-2 text-destructive">{error}</div>}
      {result && (
        <div className="mt-4 p-4 bg-muted rounded text-foreground">
          <h3 className="text-lg font-semibold mb-2">评估结果</h3>
          <p className="mb-2">
            <strong>总体评分:</strong> {result.score}/100
          </p>
          <p className="mb-2">
            <strong>反馈:</strong> {result.feedback}
          </p>
          <p className="mb-2">
            <strong>准确性:</strong> {result.accuracy}/100
          </p>
          <p className="mb-2">
            <strong>流畅性:</strong> {result.fluency}/100
          </p>
          <p className="mb-2">
            <strong>完整性:</strong> {result.completeness}/100
          </p>
          {result.suggestions.length > 0 && (
            <div className="mt-2">
              <strong>改进建议:</strong>
              <ul className="list-disc pl-5 mt-1">
                {result.suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </form>
  );
}
